import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";

export default function OwnerInvite() {
  const { token } = useParams();
  const [form, setForm] = useState({ name: "", password: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirm) {
      toast.error("Passwords do not match.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/owner/accept-invite", { token, name: form.name, password: form.password });
      setDone(true);
      toast.success("Your account is ready.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "w-full px-4 py-3 rounded-xl bg-teak border border-teak-line text-cream text-sm outline-none focus:border-gold";

  return (
    <div className="min-h-screen bg-teak acheik-bg-dark flex items-center justify-center px-4" data-testid="owner-invite-page">
      <div className="w-full max-w-md bg-teak-surface border border-teak-line rounded-2xl p-8 sm:p-10 shadow-2xl">
        <Link to="/" className="flex items-center gap-3 mb-8 hover:opacity-90 transition-opacity" data-testid="invite-brand-link">
          <img src="/bh-icon-180.png" alt="BurmeseHub logo" className="w-10 h-10 rounded-full" />
          <div>
            <p className="font-display font-extrabold text-cream text-lg leading-none">BURMESE <span className="text-gold">HUB</span></p>
            <p className="text-[11px] font-mono uppercase tracking-[0.2em] text-cream/50 mt-1">Owner Invitation</p>
          </div>
        </Link>
        {done ? (
          <div className="text-center" data-testid="invite-success">
            <CheckCircle2 className="w-12 h-12 text-jade mx-auto mb-4" />
            <p className="font-display font-bold text-cream text-xl mb-2">You're all set</p>
            <p className="text-sm text-cream/60 mb-6">Sign in to manage your listings and see your visitor analytics.</p>
            <button onClick={() => navigate("/owner/login")} className="w-full bg-gold hover:bg-gold-dark text-ink font-bold py-3.5 rounded-xl" data-testid="invite-goto-login-btn">
              Go to owner sign in
            </button>
          </div>
        ) : (
          <form onSubmit={submit} data-testid="invite-accept-form">
            <p className="text-sm text-cream/70 mb-6">Welcome. Choose your password to activate your business owner account.</p>
            <label className="block mb-4">
              <span className="block text-xs font-semibold uppercase tracking-wider text-cream/60 mb-2">Your name</span>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} placeholder="Aung Kyaw" data-testid="invite-name-input" />
            </label>
            <label className="block mb-4">
              <span className="block text-xs font-semibold uppercase tracking-wider text-cream/60 mb-2">Password</span>
              <input type="password" required minLength={8} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={inputCls} placeholder="Minimum 8 characters" data-testid="invite-password-input" />
            </label>
            <label className="block mb-7">
              <span className="block text-xs font-semibold uppercase tracking-wider text-cream/60 mb-2">Confirm password</span>
              <input type="password" required minLength={8} value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} className={inputCls} placeholder="Repeat password" data-testid="invite-confirm-input" />
            </label>
            <button type="submit" disabled={busy} className="w-full bg-gold hover:bg-gold-dark disabled:opacity-60 text-ink font-bold py-3.5 rounded-xl" data-testid="invite-accept-btn">
              {busy ? "Activating…" : "Activate my account"}
            </button>
            <p className="mt-3 text-center text-xs text-cream/50">
              By activating, you agree to our <Link to="/privacy-policy" className="text-gold hover:underline" data-testid="invite-privacy-link">Privacy Policy</Link>.
            </p>
            <p className="mt-5 text-center text-xs text-cream/40">
              Already activated? <Link to="/owner/login" className="text-gold" data-testid="invite-login-link">Sign in</Link>
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
